import Swiper from 'swiper';
import { A11y, Autoplay, Keyboard, Mousewheel, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

/**
 * Sliders: every [data-slider] element (a .swiper with a .swiper-wrapper and .swiper-slide children)
 * becomes a Swiper. The attribute holds the Swiper options as JSON:
 *   data-slider="{{ { slidesPerView: 1, autoplay: { delay: 6000 } }|json_encode }}"
 *
 * Controls are looked up in the slider's root, the closest [data-slider-root] or else the slider's parent:
 * [data-slider-prev] / [data-slider-next] for the arrows, [data-slider-pagination] for the bullets and
 * [data-slider-counter] for a "01"-style slide number. While autoplay runs, the root gets --slider-progress
 * (0 → 1) for a progress bar.
 *
 * Autoplay stays off for people who prefer reduced motion. Sliders swapped in by Sprig start on htmx:afterSettle.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function initSlider(element) {
    if (element.swiper) {
        return;
    }

    let options = {};
    try {
        options = JSON.parse(element.dataset.slider || '{}');
    } catch (error) {
        console.error('Slider: invalid data-slider JSON', error);
    }

    const root = element.closest('[data-slider-root]') ?? element.parentElement;
    const prevEl = root.querySelector('[data-slider-prev]');
    const nextEl = root.querySelector('[data-slider-next]');
    const paginationEl = root.querySelector('[data-slider-pagination]');
    const counter = root.querySelector('[data-slider-counter]');

    if (prevEl || nextEl) {
        options.navigation = { prevEl, nextEl, ...(typeof options.navigation === 'object' ? options.navigation : {}) };
    }
    if (paginationEl) {
        options.pagination = { el: paginationEl, clickable: true, ...(typeof options.pagination === 'object' ? options.pagination : {}) };
    }
    if (reducedMotion.matches) {
        options.autoplay = false;
    }

    const swiper = new Swiper(element, {
        modules: [A11y, Autoplay, Keyboard, Mousewheel, Navigation, Pagination],
        keyboard: { enabled: true, onlyInViewport: true },
        ...options,
    });

    swiper.on('slideChange', () => {
        if (counter) {
            counter.textContent = String(swiper.realIndex + 1).padStart(2, '0');
        }
        root.style.setProperty('--slider-progress', options.autoplay ? '0' : '1');
    });

    if (options.autoplay) {
        swiper.on('autoplayTimeLeft', (instance, timeLeft, progress) => {
            root.style.setProperty('--slider-progress', String(1 - progress));
        });
    }
}

function initSliders(scope = document) {
    scope.querySelectorAll('[data-slider]').forEach(initSlider);
}

document.addEventListener('DOMContentLoaded', () => initSliders());
document.addEventListener('htmx:afterSettle', (event) => initSliders(event.target));
